import { addNotification } from "portal/state/notifications";
import {
  AlmanacUpdate,
  areCategoriesEqual,
  buildDeleteCategoryChanges,
  buildRevertCategoryChange,
  buildUpdateCategoryChange,
  toDraftCategory,
  toRadioChip,
} from "portal/utils/categoryCollection/category";
import {
  Button,
  capitalize,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { Category, SavedCategory } from "protos/robot_syncer/plant_profile";
import { CategoryCollectionCustomerProvider } from "portal/components/categoryCollectionProfile/CategoryCollectionCustomerContext";
import { categoryImageToggle } from "portal/utils/categoryCollection/image";
import { CategoryTemplateEdit } from "portal/components/categoryTemplates/CategoryTemplateEdit";
import { CategoryTemplateView } from "portal/components/categoryTemplates/CategoryTemplateView";
import { CLIENT_ERROR_CODES } from "portal/utils/errors";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { convert } from "portal/utils/units/units";
import { CustomerScopeSelector } from "portal/components/customers/CustomerScopeSelector";
import { EditableTypography } from "portal/components/EditableTypography";
import { FloatingSaveButton } from "portal/components/categoryCollectionProfile/FloatingSaveButton";
import {
  getCategoryTemplateEditPath,
  getCategoryTemplatesPath,
  isCategoryTemplateEditPath,
} from "portal/utils/routing";
import { Header } from "portal/components/header/Header";
import { isEqual } from "portal/utils/equality";
import { isUndefined } from "portal/utils/identity";
import { isValidAlmanac } from "portal/utils/shootTime";
import { Loading } from "portal/components/Loading";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { OutOfDateAlert } from "portal/components/categoryCollectionProfile/OutOfDateAlert";
import { Page } from "portal/components/Page";
import { portalEndpoints } from "portal/state/portalApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { UnsavedChangesDiscardDialog } from "portal/components/unsavedChanges/UnsavedChangesDiscardDialog";
import {
  useCanUpdateCarbonCategoryTemplates,
  useCanUpdateCategoryTemplate,
} from "portal/views/categoryTemplates/permissions";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { useUnsavedChangesBlocker } from "portal/components/unsavedChanges/useUnsavedChangesBlocker";
import CarbonLogo from "portal/images/logo_icon.svg?react";
import CategoryTemplatesIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import ParentIcon from "@mui/icons-material/ArrowBackOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";
import SaveIcon from "@mui/icons-material/SaveOutlined";

export const CategoryTemplate: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const isEditing = isCategoryTemplateEditPath(pathname);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const hasUpdatePermission = useCanUpdateCategoryTemplate();
  const canUpdateCarbonTemplates = useCanUpdateCarbonCategoryTemplates();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pendingLeavePath, setPendingLeavePath] = useState<string | undefined>(
    undefined
  );

  const [applyDelete] = useMutationPopups(
    portalEndpoints.applyCategoryChanges.useMutation(),
    {
      success: capitalize(
        t("utils.actions.deletedLong", {
          subject: t("models.categoryCollectionProfiles.category_one"),
        })
      ),
    }
  );
  const [applySave, { isLoading: isSaving }] = useMutationPopups(
    portalEndpoints.applyCategoryChanges.useMutation(),
    {
      success: capitalize(
        t("utils.actions.savedLong", {
          subject: t("models.categoryCollectionProfiles.category_one"),
        })
      ),
      portalErrorMessagesByCode: {
        [CLIENT_ERROR_CODES.STALE_VERSION]: t(
          "components.categoryCollectionProfile.warnings.saveStaleConflict"
        ),
      },
    }
  );
  const [applyRevert, { isLoading: isReverting }] = useMutationPopups(
    portalEndpoints.applyCategoryChanges.useMutation(),
    {
      success: t("components.categoryTemplates.actions.restored"),
      portalErrorMessagesByCode: {
        [CLIENT_ERROR_CODES.STALE_VERSION]: t(
          "components.categoryCollectionProfile.warnings.saveStaleConflict"
        ),
      },
    }
  );

  const {
    data: latestSavedCategory,
    isLoading,
    isError,
    error,
  } = useQueryPopups(
    portalEndpoints.getCategory.useQuery(id ? { uuid: id } : skipToken, {
      pollingInterval: convert(60).from("s").to("ms"),
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
    })
  );

  const [draftBaselineCategory, setDraftBaselineCategory] = useState<
    SavedCategory | undefined
  >();
  const [draftCategory, setDraftCategory] = useState<Category | undefined>();
  const [invalidShootTimeFieldIds, setInvalidShootTimeFieldIds] = useState(
    () => new Set<string>()
  );

  const initializeEditSession = useCallback((saved: SavedCategory) => {
    if (!saved.category) {
      return;
    }
    setDraftBaselineCategory(saved);
    setDraftCategory(toDraftCategory(saved.category));
    setInvalidShootTimeFieldIds(new Set());
  }, []);

  const clearEditSession = useCallback(() => {
    setDraftBaselineCategory(undefined);
    setDraftCategory(undefined);
    setInvalidShootTimeFieldIds(new Set());
  }, []);

  if (isEditing && latestSavedCategory?.category && !draftCategory) {
    initializeEditSession(latestSavedCategory);
  }

  const formMeta = useMemo((): {
    imageDirty?: boolean;
    shootTimeDirty?: boolean;
    dirty: boolean;
  } => {
    if (!isEditing || !draftBaselineCategory?.category || !draftCategory) {
      return { dirty: false };
    }
    const imageDirty = !isEqual(
      draftBaselineCategory.category.chipIds,
      draftCategory.chipIds,
      { orderedArrays: false }
    );
    const shootTimeDirty = !isEqual(
      draftBaselineCategory.category.almanac,
      draftCategory.almanac
    );
    const dirty = !areCategoriesEqual(
      draftBaselineCategory.category,
      draftCategory
    );

    return {
      imageDirty,
      shootTimeDirty,
      dirty,
    };
  }, [draftBaselineCategory?.category, draftCategory, isEditing]);
  const hasUnsavedChanges = formMeta.dirty;
  const hasInvalidShootTimeFields = invalidShootTimeFieldIds.size > 0;
  const {
    allowNextNavigation,
    isBlocked: isNavigationBlocked,
    proceed: proceedWithNavigation,
    reset: resetBlockedNavigation,
  } = useUnsavedChangesBlocker(isEditing && hasUnsavedChanges);

  const draftBaselineVersionId = draftBaselineCategory?.metadata?.versionId;
  const isOutOfDate = Boolean(
    isEditing &&
      draftBaselineVersionId &&
      latestSavedCategory?.metadata?.versionId &&
      latestSavedCategory.metadata.versionId !== draftBaselineVersionId
  );

  const refreshToLatest = useCallback(() => {
    if (!latestSavedCategory?.category) {
      return;
    }
    initializeEditSession(latestSavedCategory);
  }, [latestSavedCategory, initializeEditSession]);

  const onBeginEdit = useCallback(() => {
    if (!latestSavedCategory?.category || !id) {
      return;
    }
    initializeEditSession(latestSavedCategory);
    navigate(getCategoryTemplateEditPath(id));
  }, [id, latestSavedCategory, navigate, initializeEditSession]);

  const category = isEditing
    ? draftCategory ?? latestSavedCategory?.category
    : latestSavedCategory?.category;

  const categoryOptions = useMemo(
    () => (category ? toRadioChip(i18n, t, [category], false) : []),
    [category, i18n, t]
  );
  const categorizationMap = useMemo(
    () =>
      Object.fromEntries(
        (category?.chipIds ?? []).map((chipId) => [chipId, category!.id])
      ),
    [category]
  );

  const onImageToggle = useCallback(
    (predictionPointId: string) => {
      if (!isEditing || !draftCategory || !predictionPointId) {
        return;
      }
      setDraftCategory(categoryImageToggle(draftCategory, predictionPointId));
    },
    [draftCategory, isEditing]
  );

  const onRevert = useCallback(
    (revertToVersionId: string) => {
      const categoryId = latestSavedCategory?.category?.id;
      const versionId = latestSavedCategory?.metadata?.versionId;
      if (!categoryId || !versionId) {
        return Promise.resolve();
      }
      return applyRevert({
        uuid: categoryId,
        change: buildRevertCategoryChange(
          categoryId,
          versionId,
          revertToVersionId
        ),
        customerUuid: latestSavedCategory.category?.customerId,
      });
    },
    [applyRevert, latestSavedCategory]
  );

  const onRevive = useCallback(
    async (predictionPointId?: string) => {
      const current = latestSavedCategory?.category;
      if (!latestSavedCategory || !current || !predictionPointId) {
        return;
      }
      if (current.chipIds.includes(predictionPointId)) {
        return;
      }
      let change;
      try {
        change = buildUpdateCategoryChange(
          latestSavedCategory,
          categoryImageToggle(toDraftCategory(current), predictionPointId)
        );
      } catch (error) {
        dispatch(
          addNotification({
            message:
              error instanceof Error
                ? error.message
                : t("components.ErrorBoundary.error"),
            variant: "error",
          })
        );
        return;
      }
      await applySave({
        uuid: current.id,
        change,
        customerUuid: current.customerId,
      });
    },
    [applySave, dispatch, latestSavedCategory, t]
  );

  const onNameEdit = useCallback(
    (name: string) => {
      if (!draftCategory) {
        return;
      }
      setDraftCategory({ ...draftCategory, name });
    },
    [draftCategory]
  );

  const onShootTimeChange = useCallback(
    (update: AlmanacUpdate) => {
      if (!draftCategory) {
        return;
      }
      const currentAlmanac = draftCategory.almanac;
      if (!isValidAlmanac(currentAlmanac)) {
        dispatch(
          addNotification({
            message: t("components.categoryTemplates.errors.invalidAlmanac"),
            variant: "error",
          })
        );
        return;
      }
      setDraftCategory({
        ...draftCategory,
        almanac: {
          ...currentAlmanac,
          sizeBoundariesMm:
            update.sizeBoundariesMm ?? currentAlmanac.sizeBoundariesMm,
          formulas: update.formulas ?? currentAlmanac.formulas,
        },
      });
    },
    [dispatch, draftCategory, t]
  );

  const onShootTimeReset = useCallback(() => {
    if (!draftCategory || !draftBaselineCategory?.category) {
      return;
    }
    setDraftCategory({
      ...draftCategory,
      almanac: draftBaselineCategory.category.almanac
        ? structuredClone(draftBaselineCategory.category.almanac)
        : undefined,
    });
    setInvalidShootTimeFieldIds(new Set());
  }, [draftBaselineCategory?.category, draftCategory]);

  const onShootTimeValidityChange = useCallback(
    (fieldId: string, isValid: boolean) => {
      setInvalidShootTimeFieldIds((prev) => {
        const has = prev.has(fieldId);
        if (isValid && !has) {
          return prev;
        }
        if (!isValid && has) {
          return prev;
        }
        const next = new Set(prev);
        if (isValid) {
          next.delete(fieldId);
        } else {
          next.add(fieldId);
        }
        return next;
      });
    },
    []
  );

  const persistCategory = useCallback(async (): Promise<
    SavedCategory | undefined
  > => {
    if (
      !draftBaselineCategory ||
      !draftCategory ||
      !hasUnsavedChanges ||
      hasInvalidShootTimeFields ||
      isSaving ||
      isOutOfDate
    ) {
      return;
    }
    let change;
    try {
      change = buildUpdateCategoryChange(draftBaselineCategory, draftCategory);
    } catch (error) {
      dispatch(
        addNotification({
          message:
            error instanceof Error
              ? error.message
              : t("components.ErrorBoundary.error"),
          variant: "error",
        })
      );
      return;
    }
    const result = await applySave({
      uuid: draftCategory.id,
      change,
      customerUuid: draftCategory.customerId,
    });
    return result.data;
  }, [
    applySave,
    dispatch,
    draftCategory,
    hasInvalidShootTimeFields,
    hasUnsavedChanges,
    draftBaselineCategory,
    isOutOfDate,
    isSaving,
    t,
  ]);

  const leaveTo = useCallback(
    (path: string) => {
      allowNextNavigation();
      setConfirmDiscard(false);
      setPendingLeavePath(undefined);
      clearEditSession();
      navigate(path);
    },
    [allowNextNavigation, clearEditSession, navigate]
  );

  const requestLeave = useCallback(
    (path: string) => {
      if (isEditing && hasUnsavedChanges) {
        setPendingLeavePath(path);
        setConfirmDiscard(true);
        return;
      }
      leaveTo(path);
    },
    [hasUnsavedChanges, isEditing, leaveTo]
  );

  const onCancel = useCallback(() => {
    if (!id) {
      return;
    }
    requestLeave(getCategoryTemplatesPath(id));
  }, [id, requestLeave]);

  const onSave = useCallback(async () => {
    const saved = await persistCategory();
    if (saved) {
      initializeEditSession(saved);
    }
  }, [initializeEditSession, persistCategory]);

  const onSaveAndClose = useCallback(async () => {
    if (!id) {
      return;
    }
    const saved = await persistCategory();
    if (saved) {
      leaveTo(getCategoryTemplatesPath(id));
    }
  }, [id, leaveTo, persistCategory]);

  const onBack = useCallback(() => {
    requestLeave(getCategoryTemplatesPath());
  }, [requestLeave]);

  if (isLoading) {
    return <Loading failed={isError} error={error} />;
  }

  if (!category || !id) {
    return <Navigate to={getCategoryTemplatesPath()} replace />;
  }

  const canUpdate =
    hasUpdatePermission && (!category.carbon || canUpdateCarbonTemplates);
  const isSaveDisabled =
    !hasUnsavedChanges || isSaving || isOutOfDate || hasInvalidShootTimeFields;

  if (isEditing && !canUpdate) {
    return <Navigate to={getCategoryTemplatesPath(id)} replace />;
  }

  return (
    <>
      <Header title={t("views.categoryTemplates.title")}>
        <CustomerScopeSelector
          readOnly
          value={category.customerId}
          className="text-white"
        />
      </Header>
      <Page>
        <div className="flex flex-col min-h-full">
          {isOutOfDate && (
            <OutOfDateAlert
              hasUnsavedChanges={hasUnsavedChanges}
              onRefresh={refreshToLatest}
            />
          )}
          <div className="flex flex-wrap flex-col md:flex-row items-start md:items-center justify-center md:justify-between md:mb-8 w-full gap-2 md:gap-4">
            <div className="flex items-center justify-start flex-none">
              <IconButton
                onClick={onBack}
                aria-label={t("views.categoryTemplates.title")}
              >
                <ParentIcon />
              </IconButton>
            </div>
            <div className="flex flex-auto items-center gap-1.5 min-w-0">
              {latestSavedCategory?.libraryMetadata && (
                <CategoryTemplatesIcon
                  fontSize="large"
                  aria-label={t("views.categoryTemplates.title")}
                />
              )}
              {category.carbon && (
                <CarbonLogo className="h-5 w-5 shrink-0 text-carbon-orange" />
              )}
              {isEditing ? (
                <EditableTypography
                  variant="h4"
                  value={category.name}
                  onEdit={onNameEdit}
                />
              ) : (
                <Typography variant="h4">{category.name}</Typography>
              )}
            </div>
            <div className="flex items-center gap-4 flex-none">
              {isEditing ? (
                <>
                  <Button
                    color="inherit"
                    variant="outlined"
                    onClick={onCancel}
                    disabled={isSaving}
                  >
                    {t("utils.actions.cancel")}
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={onSave}
                    disabled={isSaveDisabled}
                    loading={isSaving}
                    startIcon={<SaveIcon />}
                  >
                    {t("utils.actions.save")}
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={onSaveAndClose}
                    disabled={isSaveDisabled}
                    loading={isSaving}
                  >
                    {t("utils.actions.saveAndClose")}
                  </Button>
                </>
              ) : (
                canUpdate && (
                  <>
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={onBeginEdit}
                      startIcon={<EditIcon />}
                    >
                      {t("utils.actions.edit")}
                    </Button>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={() => setConfirmDelete(true)}
                      startIcon={<DeleteIcon />}
                    >
                      {t("utils.actions.delete")}
                    </Button>
                  </>
                )
              )}
              {confirmDelete && (
                <ConfirmationDialog
                  title={t("utils.actions.deleteLong", {
                    subject: t(
                      "models.categoryCollectionProfiles.category_one"
                    ),
                  })}
                  description={capitalize(
                    t("components.ConfirmationDialog.delete.description", {
                      subject: t(
                        "models.categoryCollectionProfiles.category_one"
                      ),
                    })
                  )}
                  destructive
                  yesText={t("utils.actions.delete", {
                    subject: category.name,
                  })}
                  onClose={() => setConfirmDelete(false)}
                  onYes={async () => {
                    const result = await applyDelete({
                      uuid: category.id,
                      change: buildDeleteCategoryChanges(category.id),
                      customerUuid: category.customerId,
                    });
                    if (!result.error) {
                      navigate(getCategoryTemplatesPath());
                    }
                  }}
                />
              )}
              {confirmDiscard && (
                <UnsavedChangesDiscardDialog
                  onClose={() => {
                    setConfirmDiscard(false);
                    setPendingLeavePath(undefined);
                  }}
                  onYes={() => {
                    if (!isUndefined(pendingLeavePath)) {
                      leaveTo(pendingLeavePath);
                    }
                  }}
                />
              )}
              {isNavigationBlocked && (
                <UnsavedChangesDiscardDialog
                  onClose={resetBlockedNavigation}
                  onYes={() => {
                    clearEditSession();
                    proceedWithNavigation();
                  }}
                />
              )}
            </div>
          </div>
          <Paper className="p-4 pr-8 flex flex-1 flex-col min-h-0 overflow-visible">
            <CategoryCollectionCustomerProvider
              customerId={category.customerId}
            >
              <div key={category.customerId ?? ""}>
                {isEditing ? (
                  <CategoryTemplateEdit
                    category={category}
                    categoryOptions={categoryOptions}
                    categorizationMap={categorizationMap}
                    floatingSaveAction={
                      <FloatingSaveButton
                        onClick={onSave}
                        disabled={isSaveDisabled}
                        loading={isSaving}
                      />
                    }
                    isImageDirty={Boolean(formMeta.imageDirty)}
                    onImageToggle={onImageToggle}
                    isShootTimeDirty={Boolean(formMeta.shootTimeDirty)}
                    isShootTimeInvalid={hasInvalidShootTimeFields}
                    onShootTimeChange={onShootTimeChange}
                    onShootTimeReset={onShootTimeReset}
                    onShootTimeValidityChange={onShootTimeValidityChange}
                  />
                ) : (
                  <CategoryTemplateView
                    category={category}
                    categoryOptions={categoryOptions}
                    categorizationMap={categorizationMap}
                    currentVersionId={latestSavedCategory?.metadata?.versionId}
                    onRevert={canUpdate ? onRevert : undefined}
                    isReverting={isReverting}
                    onRevive={canUpdate ? onRevive : undefined}
                  />
                )}
              </div>
            </CategoryCollectionCustomerProvider>
          </Paper>
        </div>
      </Page>
    </>
  );
};
